import React from 'react';

export const textureCompressionArticle = {
  id: 'advanced-texture-compression',
  title: 'Texture Compression Techniques',
  description: 'Get huge savings by converting, resizing and re-encoding textures.',
  seoTitle: 'GLB Texture Compression: Resize & Convert to WebP or AVIF',
  seoDescription: 'Cut GLB and glTF file size by resizing textures and converting them to WebP or AVIF. Recommended sizes, formats and quality settings for web, AR and mobile.',
  category: 'advanced',
  lastUpdated: '2025-06-29',
  tags: ['textures', 'compression', 'ktx2', 'webp', 'avif'],
  content: () => (
    <div>
  <div className="space-y-6 text-muted-foreground leading-relaxed">
        <p>
          In typical game or product-visualisation assets, <strong>textures account for 70-95 %</strong> of download size.
          This article walks through each control in the <em>Texture compression</em> section and shows when to use it.
        </p>

  <h2 className="text-xl font-semibold text-foreground mb-3">Why compress textures?</h2>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>Faster initial load → better Core Web Vitals.</li>
          <li>Lower memory footprint on mobile GPUs.</li>
          <li>Cheaper CDN / S3 bandwidth bills.</li>
        </ul>

  <h2 className="text-xl font-semibold text-foreground mb-3">Image format</h2>
        <p>
          Choose the container that balances quality, transparency and browser support:
        </p>
  <table className="w-full text-sm text-left border-collapse border border mb-4">
          <thead>
            <tr className="bg-background-elevated text-foreground">
              <th className="p-2 border border">Format</th>
              <th className="p-2 border border">Pros</th>
              <th className="p-2 border border">Cons</th>
              <th className="p-2 border border">Best for</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="p-2 border border">JPEG</td>
              <td className="p-2 border border">Tiny files, universal support</td>
              <td className="p-2 border border">No alpha, lossy</td>
              <td className="p-2 border border">Photos, baked lightmaps</td>
            </tr>
            <tr>
              <td className="p-2 border border">PNG</td>
              <td className="p-2 border border">Lossless, alpha channel</td>
              <td className="p-2 border border">Large files</td>
              <td className="p-2 border border">Logos, decals, UI sprites</td>
            </tr>
            <tr>
              <td className="p-2 border border">WebP</td>
              <td className="p-2 border border">25-35 % smaller than PNG, full alpha</td>
              <td className="p-2 border border">Older Safari pre-16.0 lacks support</td>
              <td className="p-2 border border">Most 3D models</td>
            </tr>
            <tr>
              <td className="p-2 border border">AVIF</td>
              <td className="p-2 border border">50 % smaller than WebP, HDR support</td>
              <td className="p-2 border border">Decoding is slower; limited Safari</td>
              <td className="p-2 border border">High-res hero textures</td>
            </tr>
          </tbody>
        </table>
        <p className="text-sm text-muted">
          Explore browser support: <a href="https://caniuse.com/avif" target="_blank" rel="noopener noreferrer" className="underline">caniuse.com</a>.
        </p>

  <h2 className="text-xl font-semibold text-foreground mb-3">Max dimensions</h2>
        <p>
          Large textures kill performance even at high GPU memory budgets.  Limit dimensions to powers of two for best
          mip-mapping. For Web - 2048 px is a sweet spot.
        </p>

  <h2 className="text-xl font-semibold text-foreground mb-3">Quality slider</h2>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li><strong>90-100</strong> – Near-lossless. Use for hero assets viewed up close.</li>
          <li><strong>70-85</strong> – Default. Visual difference is negligible; halves file size vs lossless.</li>
          <li><strong>&lt;60</strong> – Only for distant props or mobile where bandwidth is at a premium.</li>
        </ul>

  <h2 className="text-xl font-semibold text-foreground mb-3">Consider KTX2 + BasisU</h2>
        <p>
          For runtime decoding on the GPU, container formats like KTX2 with <a href="https://github.com/BinomialLLC/basis_universal" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/90">Basis&nbsp;Universal</a>{' '}
          are superior – they deliver BC7/ETC/RGTC sized blocks directly to texture memory.  We plan to expose this
          option in a future update.
        </p>
      </div>
    </div>
  )
}; 